import { binaryMaskFromImageData } from './binaryMaskFromImageData';
import { computeMaskBoundingBox } from './computeMaskBoundingBox';
import { extractSlatSegments } from './extractSlatSegments';
import { mapSegmentsToViewBox } from './mapSegmentsToViewBox';

/**
 * Full pipeline: ImageData → rounded slat rects in viewBox units.
 *
 * @param {ImageData} imageData
 * @param {{ width: number, height: number }} viewBox
 * @param {object} [classifyOptions] - passed to binaryMaskFromImageData
 * @param {object} [layoutOptions] - padding, barFillRatio, opacity, minRunWidthPx
 * @returns {{
 *   rects: { x: number, y: number, w: number, h: number, rx: number, opacity: number }[],
 *   bbox: { minX: number, minY: number, maxX: number, maxY: number } | null,
 *   gridWidth: number,
 *   gridHeight: number,
 *   mask: Uint8Array
 * }}
 */
export function imageDataToSlatRects(imageData, viewBox, classifyOptions = {}, layoutOptions = {}) {
  const { mask, width, height } = binaryMaskFromImageData(imageData, classifyOptions);
  const bbox = computeMaskBoundingBox(mask, width, height);
  if (!bbox) {
    return { rects: [], bbox: null, gridWidth: width, gridHeight: height, mask };
  }

  const minRun = layoutOptions.minRunWidthPx ?? 1;
  const rows = extractSlatSegments(mask, width, height, minRun);
  const rects = mapSegmentsToViewBox(rows, bbox, viewBox, layoutOptions);

  return { rects, bbox, gridWidth: width, gridHeight: height, mask };
}

/**
 * @param {Uint8Array} mask
 * @param {number} width
 * @param {number} height
 * @param {{ width: number, height: number }} viewBox
 * @param {object} [layoutOptions]
 */
export function maskToSlatRects(mask, width, height, viewBox, layoutOptions = {}) {
  const bbox = computeMaskBoundingBox(mask, width, height);
  if (!bbox) {
    return { rects: [], bbox: null };
  }
  const minRun = layoutOptions.minRunWidthPx ?? 1;
  const rows = extractSlatSegments(mask, width, height, minRun);
  const rects = mapSegmentsToViewBox(rows, bbox, viewBox, layoutOptions);
  return { rects, bbox };
}
